/**
 * Ksting
 *
 * Copyright © 2017 Chris Andersson <chris@wbisbi.com>. All rights reserved.
 *
 * This source code is licensed under the MIT license found in the
 * LICENSE file in the root directory of this source tree.
 */

import React, { Component } from 'react';
import { shape, func, object, string, bool } from 'prop-types';
import { connect } from 'react-redux';
import { withRouter } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';

import { getPost as getPostAction } from 'actions/blog';

import FormatDate from 'components/FormatDate/FormatDate';
import SpinnerIcon from 'components/SpinnerIcon/SpinnerIcon';
import MetaTags from 'components/MetaTags/MetaTags';
import MapWPCategories from 'components/MapWPCategories/MapWPCategories';
import BackButton from 'components/BackButton/BackButton';

import styles from './blog-post.scss';

/**
 * This is the BlogPost component.
 *
 * @author Chris Andersson <chris@wbisbi.com>
 */
export class BlogPost extends Component {
  /**
   * Declare expected prop types.
   *
   * @type {object}
   */
  static propTypes = {
    match: shape({
      params: shape({
        slug: string.isRequired,
      }).isRequired,
    }).isRequired,
    blogPost: shape({
      loading: bool.isRequired,
      title: string,
      date: string,
      content: string,
      excerpt: string,
      featured_image: string,
      categories: object,
    }).isRequired,
    getPost: func.isRequired,
  };

  componentDidMount() {
    const {
      match: { params },
      blogPost,
      getPost,
    } = this.props;

    const loadPost = blogPost.slug !== params.slug;

    if (loadPost) {
      getPost(params.slug);
    }
  }

  renderHtmlLang = () => {
    const {
      blogPost: { tags },
    } = this.props;

    const localeTags = {
      english: 'en',
      swedish: 'sv',
    };

    const currentLang = Object.keys(tags).find(tag =>
      // get the localeTags as array with only keys
      // and check if the currentLang exists.
      Object.keys(localeTags)
        .map(localeTag => localeTag)
        .includes(tag)
    );
    const locale = currentLang ? localeTags[currentLang] : 'en';

    return <Helmet defer={false} htmlAttributes={{ lang: locale }} />;
  };

  /**
   * Render BlogPost.
   *
   * @return {Object}
   */
  render() {
    const {
      blogPost: {
        title,
        date,
        excerpt,
        featured_image,
        content,
        categories,
        loading,
      },
    } = this.props;

    if (loading) {
      return (
        <section className={styles.wrapper}>
          <div className={styles.spinner}>
            <SpinnerIcon medium />
          </div>
        </section>
      );
    }

    return (
      <section className={styles.wrapper}>
        <MetaTags title={title} description={excerpt} image={featured_image} />

        {this.renderHtmlLang()}

        <BackButton />

        <header className={styles.header}>
          <div className={styles.headerContent}>
            <div className={styles.headerRow}>
              <div className={styles.headerText}>
                <div className={styles.meta}>
                  <FormatDate className={styles.date} date={date} />

                  <ul className={styles.categories}>
                    <MapWPCategories
                      categories={categories}
                      className={styles.category}
                    />
                  </ul>
                </div>

                <h1
                  className={styles.title}
                  dangerouslySetInnerHTML={{ __html: title }}
                />
              </div>
            </div>
          </div>
        </header>

        <div className={styles.content}>
          <div className={styles.row}>
            <div className={styles.post}>
              <div
                className={styles.text}
                dangerouslySetInnerHTML={{ __html: content }}
              />
            </div>
          </div>
        </div>
      </section>
    );
  }
}

export default withRouter(
  connect(({ auth, blogPost }) => ({ auth, blogPost }), {
    getPost: getPostAction,
  })(BlogPost)
);
